/**
 * The row above a field: what the figure is on the left, the figure on the
 * right ("Available to trade            1.5 BTC").
 *
 * Two rules it exists to enforce:
 *  - the figure is only clickable when clicking it would actually fill the
 *    field. A balance that looks like a MAX button and fills 0 costs more trust
 *    than one that was never clickable.
 *  - a figure that is not known renders as "—". "undefined USDC" and "NaN" have
 *    both shipped to traders from rows like this one.
 */
import { useId } from "react";
import { MaxResult } from "../../../utils/amount/max/types";
import { UNKNOWN_DISPLAY } from "../../../utils/unknownNumbers";
import { HintText, MetaLabel, MetaRow, MetaValue } from "./style";

// Deliberately the shared marker, not a local em-dash: two glyphs for
// "unknown" in one screen is not a design, it is a drift.

/** Strings that are a missing figure wearing a number's clothes. */
const NOT_A_FIGURE = new Set(["nan", "undefined", "null", "infinity", "-"]);

const isFigure = (text?: string): boolean => {
  if (text === undefined || text === null) return false;
  const trimmed = String(text).trim();
  if (trimmed === "") return false;
  return !NOT_A_FIGURE.has(trimmed.toLowerCase());
};

export interface InputMetaProps {
  label: string;
  /** Already formatted for display — this row never does the maths. */
  valueText?: string;
  unit?: string;
  /** Fills the field with the maximum. Omit where the figure is read-only. */
  onValueClick?: () => void;
  /** The maximum behind the figure; an unavailable one is not clickable. */
  max?: MaxResult;
  /** Human text for why it cannot be used right now. Shown, not swallowed. */
  disabledReason?: string;
}

export const InputMeta = ({
  label,
  valueText,
  unit,
  onValueClick,
  max,
  disabledReason,
}: InputMetaProps) => {
  const generatedId = useId();
  const hintId = `${generatedId}-meta-reason`;

  const known = isFigure(valueText);
  const figure = known
    ? [String(valueText).trim(), unit].filter(Boolean).join(" ")
    : UNKNOWN_DISPLAY;

  const wantsClick = onValueClick !== undefined;
  const clickable = wantsClick && max?.ok === true && disabledReason === undefined;
  const blocked = wantsClick && disabledReason !== undefined;

  return (
    <>
      <MetaRow>
        <MetaLabel>{label}</MetaLabel>
        {clickable ? (
          <MetaValue type="button" $interactive onClick={onValueClick}>
            {figure}
          </MetaValue>
        ) : blocked ? (
          <MetaValue
            type="button"
            $interactive={false}
            disabled
            title={disabledReason}
            aria-describedby={hintId}
          >
            {figure}
          </MetaValue>
        ) : (
          <MetaValue as="span" $interactive={false}>
            {figure}
          </MetaValue>
        )}
      </MetaRow>
      {disabledReason !== undefined ? (
        <HintText id={hintId} role="note">
          {disabledReason}
        </HintText>
      ) : null}
    </>
  );
};
